
import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Container,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
  Chip
} from '@mui/material'

import EmailIcon from '@mui/icons-material/Email'
import SpeedIcon from '@mui/icons-material/Speed'
import TimelineIcon from '@mui/icons-material/Timeline'
import DashboardIcon from '@mui/icons-material/Dashboard'
import GroupIcon from '@mui/icons-material/Group'

export interface LandingPageProps {
  /**
   * Called when user clicks demo request button
   */
  onDemoRequest?: () => void
  
  /**
   * Custom contact email for demo requests
   * @default 'cx@erdosmiller.com'
   */
  contactEmail?: string
  
  /**
   * Custom hero title
   */
  heroTitle?: string
  
  /**
   * Custom hero subtitle
   */
  heroSubtitle?: string
}

export function LandingPage({ 
  onDemoRequest,
  contactEmail = 'cx@erdosmiller.com',
  heroTitle = 'The Complete MWD Data Analysis Platform',
  heroSubtitle = 'Bring drilling memory data from different products together to build the full picture of what\'s happening downhole. Organize your work in shareable projects so your entire organization can collaborate and learn from every analysis.'
}: LandingPageProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  
  // State for the before/after fade transition
  const [showAfter, setShowAfter] = useState(false)
  
  // Auto-toggle the before/after every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setShowAfter(prev => !prev)
    }, 4000)
    
    return () => clearInterval(interval)
  }, [])
  
  const handleDemoRequest = () => {
    if (onDemoRequest) {
      onDemoRequest()
    } else {
      window.location.href = `mailto:${contactEmail}?subject=Wave Demo Request&body=Hi, I'm interested in getting access to Wave for drilling data visualization. Please let me know next steps.`
    }
  }

  const features = [
    {
      icon: <DashboardIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Multi-Vendor Data Integration',
      description: 'Open data from multiple manufacturers automatically. Wave handles the details of different file formats so you can focus on analysis.'
    },
    {
      icon: <TimelineIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Drilling Process Understanding',
      description: 'Analyze the full drilling process with synchronized multi-axis visualization. Understand what really happened downhole.'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Feature Performance Review',
      description: 'Track and review performance of new MWD features during rollout. Data-driven insights for product development.'
    },
    {
      icon: <GroupIcon sx={{ fontSize: 40, color: 'white' }} />,
      title: 'Collaborative Project Management',
      description: 'Organize data in shareable projects. All files, time shifts, and device settings are saved. Share projects across your organization to get more eyeballs on problems and generate better insights.'
    }
  ]

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundImage: 'url(/backgrounds/Erdos_20220914_8386.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      position: 'relative',
      paddingTop: { xs: '56px', sm: '64px' } // Account for fixed header height
    }}>
      {/* Background overlay for better text readability */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.4)', // Dark overlay
        zIndex: 1
      }} />
      
      {/* Content container */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {/* Hero Section */}
        {/* Full-width Hero Title Strip */}
        <Box sx={{
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          bgcolor: 'rgba(0, 0, 0, 0.85)',
          backdropFilter: 'blur(10px)',
          py: { xs: 4, md: 6 },
          mb: { xs: 6, md: 8 },
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <Container maxWidth="xl">
            <Typography 
              variant={isMobile ? 'h2' : 'h1'} 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 800,
                mb: 3,
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                fontSize: { xs: '2rem', sm: '2.8rem', md: '3.5rem' },
                lineHeight: { xs: 1.1, md: 1.2 }
              }}
            >
              {heroTitle}
            </Typography>
            
            <Typography 
              variant="h6" 
              paragraph
              sx={{ 
                mb: 0,
                lineHeight: 1.6,
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: 'rgba(255, 255, 255, 0.9)',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                maxWidth: '800px',
                mx: 'auto'
              }}
            >
              {heroSubtitle}
            </Typography>
          </Container>
        </Box>

        <Container maxWidth="xl">

          {/* 2/3 + 1/3 Split: Demo + CTA */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: 'stretch',
            gap: { xs: 3, lg: 4 },
            bgcolor: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '20px',
            p: { xs: 3, md: 4 },
            mb: { xs: 6, md: 8 }
          }}>
            {/* Left side - Before/After Demo (2/3 width) */}
            <Box sx={{ 
              flex: { xs: '1', lg: '2' }, 
              width: '100%',
              maxWidth: { xs: '100%', lg: '66.67%' }
            }}>
              {/* Before/After Demo */}
              <Box sx={{
                position: 'relative',
                height: { xs: '600px', md: '720px' },
                borderRadius: '16px',
                overflow: 'hidden',
                cursor: 'pointer',
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
              }}
              onClick={() => setShowAfter(!showAfter)}
              >
                {/* Before View */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    bgcolor: 'rgba(90, 20, 20, 0.95)', // Darker muted red
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'opacity 0.8s ease-in-out',
                    opacity: showAfter ? 0 : 1,
                    zIndex: showAfter ? 1 : 2
                  }}
                >
                  {/* Before Content - Top Section */}
                  <Box sx={{ 
                    px: { xs: 2, md: 3 },
                    pt: { xs: 2, md: 3 },
                    pb: { xs: 1, md: 1.5 },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 3 },
                    flexShrink: 0
                  }}>
                    {/* Left: Chip and Title */}
                    <Box sx={{ flex: 1 }}>
                      <Chip 
                        label="BEFORE" 
                        sx={{ 
                          mb: 1.5, 
                          fontWeight: 'bold', 
                          fontSize: '0.9rem',
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                      />
                      <Typography variant="h5" gutterBottom fontWeight="600" sx={{ mb: 0, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                        Fighting Excel for Hours
                      </Typography>
                    </Box>
                    
                    {/* Right: Bullet Points */}
                    <Box sx={{ flex: 1 }}>
                      <Stack spacing={1}>
                        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>• Hours wrestling Excel for basic insights</Typography>
                        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>• Lose all work when closing spreadsheets</Typography>
                        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>• No way to share complex analyses</Typography>
                        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>• Zero organizational memory of findings</Typography>
                      </Stack>
                    </Box>
                  </Box>
                  
                  {/* Before Image */}
                  <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, md: 3 },
                    pb: { xs: 2, md: 3 }
                  }}>
                    <img 
                      src="/screenshots/before.png" 
                      alt="Excel drilling data analysis" 
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: 'none',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        border: 'none'
                      }}
                    />
                  </Box>
                </Box>

                {/* After View */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    bgcolor: 'rgba(15, 55, 35, 0.95)', // Darker muted green
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'opacity 0.8s ease-in-out',
                    opacity: showAfter ? 1 : 0,
                    zIndex: showAfter ? 2 : 1
                  }}
                >
                  {/* After Content - Top Section */}
                  <Box sx={{ 
                    px: { xs: 2, md: 3 },
                    pt: { xs: 2, md: 3 },
                    pb: { xs: 1, md: 1.5 },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 2, sm: 3 },
                    flexShrink: 0
                  }}>
                    {/* Left: Chip and Title */}
                    <Box sx={{ flex: 1 }}>
                      <Chip 
                        label="AFTER" 
                        sx={{ 
                          mb: 1.5, 
                          fontWeight: 'bold', 
                          fontSize: '0.9rem',
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '1px solid rgba(255, 255, 255, 0.3)'
                        }}
                      />
                      <Typography variant="h5" gutterBottom fontWeight="600" sx={{ mb: 0, fontSize: { xs: '1.3rem', md: '1.5rem' } }}>
                        Instant Insights, Zero Frustration
                      </Typography>
                    </Box>
                    
                    {/* Right: Bullet Points */}
                    <Box sx={{ flex: 1 }}>
                      <Stack spacing={1}>
                        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>• Drop files in, get charts in seconds</Typography>
                        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>• All data saved in organized projects</Typography>
                        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>• Share projects for team collaboration</Typography>
                        <Typography variant="body1" sx={{ fontSize: { xs: '1rem', md: '1.1rem' } }}>• Organizational memory of all insights</Typography>
                      </Stack>
                    </Box>
                  </Box>
                  
                  {/* After Image */}
                  <Box sx={{ 
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, md: 3 },
                    pb: { xs: 2, md: 3 }
                  }}>
                    <img 
                      src="/screenshots/wave_full_app.png" 
                      alt="Wave drilling data visualization" 
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: 'none',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        border: 'none'
                      }}
                    />
                  </Box>
                </Box>

                {/* Click indicator */}
                <Box sx={{
                  position: 'absolute',
                  bottom: 8,
                  right: 8,
                  bgcolor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.7rem',
                  color: 'white',
                  zIndex: 3
                }}>
                  Click to toggle • Auto-switches every 4s
                </Box>
              </Box>
            </Box>

            {/* Right side - CTA (1/3 width) */}
            <Box sx={{ 
              flex: { xs: '1', lg: '1' }, 
              width: '100%',
              maxWidth: { xs: '100%', lg: '33.33%' },
              height: { xs: '600px', md: '720px' }, // Match before/after demo height
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              p: { xs: 2, md: 3 },
              bgcolor: 'rgba(0, 0, 0, 0.8)',
              borderRadius: '16px',
              border: '2px solid rgba(255,255,255,0.3)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
            }}>
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                  mb: 2
                }}
              >
                Ready to Stop Fighting Excel?
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255,255,255,0.9)',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                  mb: 3,
                  lineHeight: 1.4
                }}
              >
                Get instant access to Wave and transform your drilling data analysis workflow.
              </Typography>

              <Button
                variant="outlined"
                size="large"
                startIcon={<EmailIcon />}
                onClick={handleDemoRequest}
                sx={{ 
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  minWidth: '200px',
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                  }
                }}
              >
                Request Access
              </Button>
            </Box>
          </Box>
        </Container>



      {/* Features Section */}
      <Box sx={{ 
        py: { xs: 3, md: 4 },
        bgcolor: 'rgba(0, 0, 0, 0.7)' // Darker, no blur
      }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            component="h2" 
            textAlign="center" 
            gutterBottom
            sx={{ 
              mb: 6, 
              fontWeight: 600,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            Built for Everyone in Measurement While Drilling
          </Typography>
          
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(4, 1fr)' 
            }, 
            gap: 4 
          }}>
            {features.map((feature, index) => (
              <Card key={index} sx={{ 
                height: '100%', 
                textAlign: 'center',
                bgcolor: 'rgba(0, 0, 0, 0.8)', // Much darker background
                borderRadius: '20px', // More rounded
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="600" sx={{ color: 'white' }}>
                    {feature.title}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Supported File Types */}
      <Box sx={{ 
        bgcolor: 'rgba(0, 0, 0, 0.4)', 
        py: { xs: 3, md: 4 },
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Container maxWidth="xl">
          <Typography 
            variant="h4" 
            component="h2" 
            textAlign="center" 
            gutterBottom
            sx={{ 
              mb: 6, 
              fontWeight: 600,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            Multi-Vendor Data Integration
          </Typography>
          
          <Box sx={{ 
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            alignItems: 'stretch',
            gap: { xs: 4, lg: 6 }
          }}>
            {/* Left side - Vendor & Format Support (2/3 width) */}
            <Box sx={{ 
              flex: { xs: '1', lg: '2' }, 
              width: '100%',
              maxWidth: { xs: '100%', lg: '66.67%' }
            }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography 
                  variant="h6" 
                  paragraph
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                  }}
                >
                  Wave currently supports data from multiple manufacturers, with more being added regularly
                </Typography>
              </Box>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  textAlign: 'center',
                  mb: 2,
                  color: 'white',
                  fontWeight: 600
                }}
              >
                Supported Systems
              </Typography>
              <Stack 
                direction="row" 
                spacing={2} 
                justifyContent="center" 
                flexWrap="wrap"
                useFlexGap
                sx={{ mb: 2 }}
              >
                {[
                  'Erdos Miller', 'JAE', 'Innova', 'DTech', 'Pason', 'Vertex', 'Eclipse Touch'
                ].map((system) => (
                  <Chip
                    key={system}
                    label={system}
                    variant="filled"
                    sx={{ 
                      fontSize: '0.9rem',
                      py: 0.5,
                      px: 1.5,
                      fontWeight: 500,
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      color: 'rgba(0, 0, 0, 0.87)',
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 1)'
                      }
                    }}
                  />
                ))}
              </Stack>
            </Box>

            {/* Right side - Custom Integration CTA (1/3 width) */}
            <Box sx={{ 
              flex: { xs: '1', lg: '1' }, 
              width: '100%',
              maxWidth: { xs: '100%', lg: '33.33%' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
              bgcolor: 'rgba(0, 0, 0, 0.8)',
              borderRadius: '20px',
              border: '2px solid rgba(255,255,255,0.3)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
              minHeight: { xs: 'auto', lg: '300px' }
            }}>
              <Typography 
                variant="h5" 
                gutterBottom
                sx={{ 
                  fontWeight: 600,
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                  mb: 2
                }}
              >
                Proprietary Systems?
              </Typography>
              
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255,255,255,0.9)',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                  mb: 3,
                  lineHeight: 1.4
                }}
              >
                Have in-house proprietary downhole systems? We can add support quickly!
              </Typography>

              <Button
                variant="outlined"
                size="large"
                onClick={() => window.location.href = `mailto:${contactEmail}?subject=Custom System Integration Request&body=Hi Erdos Miller Team,%0A%0AI'm interested in adding support for our proprietary in-house MWD systems to Wave.%0A%0APlease contact me to discuss integration possibilities.%0A%0AThanks!`}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem',
                  minWidth: '220px',
                  bgcolor: 'rgba(0, 0, 0, 0.8)',
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.9)',
                    borderColor: 'rgba(255, 255, 255, 0.8)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                  }
                }}
              >
                Request Custom Integration
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box sx={{ 
        py: { xs: 3, md: 4 }, 
        textAlign: 'center',
        bgcolor: 'rgba(0, 0, 0, 0.4)'
      }}>
        <Container maxWidth="md">
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom
            sx={{ 
              fontWeight: 600, 
              mb: 3,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
            }}
          >
            Ready to See the Complete Downhole Picture?
          </Typography>
          
          <Typography 
            variant="h6" 
            paragraph
            sx={{ 
              mb: 4,
              color: 'rgba(255,255,255,0.9)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            }}
          >
            Join MWD professionals who've transformed their drilling operations with integrated data analysis.
          </Typography>

          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="outlined"
              size="large"
              startIcon={<EmailIcon />}
              onClick={handleDemoRequest}
              sx={{ 
                py: 1.5,
                px: 4,
                fontSize: '1.1rem',
                bgcolor: 'rgba(0, 0, 0, 0.8)',
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.8)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                }
              }}
            >
              Get Access Today
            </Button>
          </Stack>
          
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 3,
              color: 'rgba(255,255,255,0.8)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            }}
          >
            Questions? Email <strong style={{ color: 'white' }}>{contactEmail}</strong> for support.
          </Typography>
        </Container>
      </Box>
      </Box> {/* Close content container */}
    </Box>
  )
}